import { Result } from "@cartographerio/fp";
import { isPoint } from "@cartographerio/geometry";
import { hasKey, hasOptionalKey, isArrayOf, isBoolean, isNullable, isNumber, isObject, isRecordOf, isString, } from "@cartographerio/guard";
import { MrsHorizontalOrientationEnum, MrsRiverWoodArtificialElementEnum, MrsRiverWoodBarLocationEnum, MrsRiverWoodFeatureLocationEnum, MrsRiverWoodRetainedByArtificialFeatureEnum, MrsRiverWoodRetainedByNaturalFeatureEnum, MrsRiverWoodTypeEnum, MrsUadEnum, UrsSurveyBankEnum, } from "@cartographerio/inventory-enums";
import { isOtherSpecify } from "@cartographerio/topo-form";
import { isAttachmentFolder, isTimestamp, } from "@cartographerio/types";
function isPartialGeneralInformation(data) {
    return (hasOptionalKey(data, "recorded", isNullable(isTimestamp)) &&
        hasKey(data, "bedVisible", isBoolean) &&
        hasKey(data, "adverseConditions", isBoolean) &&
        hasOptionalKey(data, "adverseConditionsNotes", isNullable(isString)) &&
        hasOptionalKey(data, "surveyBank", isNullable(UrsSurveyBankEnum.isValue)) &&
        hasOptionalKey(data, "projectName", isNullable(isString)) &&
        hasOptionalKey(data, "riverName", isNullable(isString)) &&
        hasOptionalKey(data, "reachName", isNullable(isString)) &&
        hasOptionalKey(data, "woodNumber", isNullable(isString)) &&
        hasKey(data, "photographs", isAttachmentFolder) &&
        hasOptionalKey(data, "section3Completed", isBoolean) &&
        hasOptionalKey(data, "section4Completed", isBoolean) &&
        hasOptionalKey(data, "section5Completed", isBoolean));
}
function isCharacter(data) {
    return (hasOptionalKey(data, "woodLocation", isNullable(isPoint)) &&
        hasKey(data, "cutByBeavers", isBoolean) &&
        hasKey(data, "presenceOfBeavers", isBoolean) &&
        hasOptionalKey(data, "evidenceOfBeavers", isNullable(isString)) &&
        hasKey(data, "artificialElements", isOtherSpecify(isArrayOf(MrsRiverWoodArtificialElementEnum.isValue))) &&
        hasOptionalKey(data, "woodType", isNullable(MrsRiverWoodTypeEnum.isValue)) &&
        hasOptionalKey(data, "isolatedTreeLeaningTree", isNullable(isBoolean)) &&
        hasOptionalKey(data, "isolatedTreeFallenTree", isNullable(isBoolean)) &&
        hasOptionalKey(data, "isolatedTreeHighJam", isNullable(isBoolean)) &&
        hasOptionalKey(data, "woodAccumulationHighJam", isNullable(isBoolean)) &&
        hasOptionalKey(data, "woodAccumulationPartialJam", isNullable(isBoolean)) &&
        hasOptionalKey(data, "woodAccumulationCompleteJam", isNullable(isBoolean)) &&
        hasOptionalKey(data, "hydraulicTypeOverride", isNullable(isString)));
}
function isChannelDimensions(data) {
    return (hasOptionalKey(data, "riverLocation", isNullable(isPoint)) &&
        hasOptionalKey(data, "morphRiverWidth", isNullable(isNumber)) &&
        hasOptionalKey(data, "leftBankHeight", isNullable(isNumber)) &&
        hasOptionalKey(data, "rightBankHeight", isNullable(isNumber)) &&
        hasOptionalKey(data, "bankfullWidth", isNullable(isNumber)) &&
        hasOptionalKey(data, "waterWidth", isNullable(isNumber)) &&
        hasOptionalKey(data, "waterDepth", isNullable(isNumber)));
}
function isUadRecordOf(isA) {
    return isRecordOf(MrsUadEnum.isValue, isNullable(isA));
}
function isAnchorage(data) {
    return (hasKey(data, "artificialFeatures", isOtherSpecify(isArrayOf(MrsRiverWoodRetainedByArtificialFeatureEnum.isValue))) &&
        hasKey(data, "naturalFeatures", isArrayOf(MrsRiverWoodRetainedByNaturalFeatureEnum.isValue)) &&
        hasKey(data, "pool", isUadRecordOf(isNumber)) &&
        hasKey(data, "riffle", isUadRecordOf(isNumber)) &&
        hasKey(data, "unvegetatedBar", isUadRecordOf(MrsRiverWoodBarLocationEnum.isValue)) &&
        hasKey(data, "vegetatedBar", isUadRecordOf(MrsRiverWoodBarLocationEnum.isValue)) &&
        hasKey(data, "bermOrBench", isUadRecordOf(MrsRiverWoodFeatureLocationEnum.isValue)) &&
        hasKey(data, "island", isUadRecordOf(isNumber)) &&
        hasKey(data, "erodingBankFace", isUadRecordOf(MrsRiverWoodFeatureLocationEnum.isValue)) &&
        hasKey(data, "undercutBankFace", isUadRecordOf(MrsRiverWoodFeatureLocationEnum.isValue)) &&
        hasKey(data, "sideChannel", isUadRecordOf(isNumber)));
}
function isJamDimensions(data) {
    return (hasOptionalKey(data, "morphRiverWidth", isNullable(isNumber)) &&
        hasOptionalKey(data, "acrossChannelWidth", isNullable(isNumber)) &&
        hasOptionalKey(data, "jamLength", isNullable(isNumber)) &&
        hasOptionalKey(data, "jamMaxHeightAboveChannelBed", isNullable(isNumber)) &&
        hasOptionalKey(data, "bottomOfJamHeightAboveChannelBed", isNullable(isNumber)) &&
        hasOptionalKey(data, "horizontalOrientationOfJam", isNullable(MrsHorizontalOrientationEnum.isValue)) &&
        hasOptionalKey(data, "largestWoodPieceLength", isNullable(isNumber)) &&
        hasOptionalKey(data, "largestWoodPieceDiameter", isNullable(isNumber)));
}
export function isPartialData(data) {
    return (isObject(data) &&
        hasKey(data, "generalInformation", isPartialGeneralInformation) &&
        hasKey(data, "character", isCharacter) &&
        hasKey(data, "channelDimensions", isChannelDimensions) &&
        hasKey(data, "anchorage", isAnchorage) &&
        hasKey(data, "dimensions", isJamDimensions));
}
const g = Result.guard(isPartialData, "PartialData");
export function dataDescription(data) {
    return g(data).map((data) => [
        data.generalInformation.riverName ?? "Unknown River",
        data.generalInformation.reachName ?? "Unknown Reach",
        data.generalInformation.woodNumber ?? "Unknown Jam Number",
    ].join(", "));
}
export function dataTimestamp(data) {
    return g(data).map((data) => data.generalInformation.recorded ?? null);
}
export function dataGeometry(data) {
    return g(data).map((data) => data.character.woodLocation ?? null);
}
export function copyData(data) {
    return g(data).map(data => ({
        ...data,
        generalInformation: {
            ...data.generalInformation,
            bedVisible: false,
            adverseConditions: false,
            adverseConditionsNotes: null,
        },
    }));
}
//# sourceMappingURL=parts.js.map